// EventStepGeneral.tsx
import React, { useState, useEffect } from 'react';
import { GeneralEventData, EventType } from '../../../types/EventTypes';

interface Props {
  onNext: (data: GeneralEventData) => void;
  initialData?: GeneralEventData;
}

const EventStepGeneral: React.FC<Props> = ({ onNext, initialData }) => {
  const [formData, setFormData] = useState<GeneralEventData>({
    eventType: initialData?.eventType || 'league',
    date: initialData?.date || '',
    startTime: initialData?.startTime || '',
    endTime: initialData?.endTime || '',
    location: initialData?.location || '',
    difficulty: initialData?.difficulty || 'medium',
    notes: initialData?.notes || '',
  });

  useEffect(() => {
    if (initialData) setFormData(initialData);
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onNext(formData);
  };

  const isEditing = !!initialData?.id;

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <h2 className='text-xl font-bold'>Step 1: Event Details</h2>

      <label className='block'>
        <span className='text-sm font-medium'>Event Type</span>
        <select
          name='eventType'
          value={formData.eventType}
          onChange={handleChange}
          className='p-2 border rounded w-full'
          required
          disabled={isEditing}
        >
          <option value='league'>League Match</option>
          <option value='friendly'>Friendly</option>
          <option value='training'>Training</option>
          <option value='tournament'>Tournament</option>
        </select>
      </label>

      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        <label>
          <span className='text-sm font-medium'>Date</span>
          <input
            type='date'
            name='date'
            value={formData.date || ''}
            onChange={handleChange}
            className='p-2 border rounded w-full'
            required
          />
        </label>

        <label>
          <span className='text-sm font-medium'>Start Time</span>
          <input
            type='time'
            name='startTime'
            value={formData.startTime || ''}
            onChange={handleChange}
            className='p-2 border rounded w-full'
            required
          />
        </label>

        <label>
          <span className='text-sm font-medium'>End Time (optional)</span>
          <input
            type='time'
            name='endTime'
            value={formData.endTime || ''}
            onChange={handleChange}
            className='p-2 border rounded w-full'
          />
        </label>

        <label>
          <span className='text-sm font-medium'>Difficulty</span>
          <select
            name='difficulty'
            value={formData.difficulty || ''}
            onChange={handleChange}
            className='p-2 border rounded w-full'
          >
            <option value='easy'>Easy</option>
            <option value='medium'>Medium</option>
            <option value='hard'>Hard</option>
          </select>
        </label>
      </div>

      <label className='block'>
        <span className='text-sm font-medium'>Location / Postcode</span>
        <input
          type='text'
          name='location'
          value={formData.location || ''}
          onChange={handleChange}
          className='p-2 border rounded w-full'
          required
        />
      </label>

      <label className='block'>
        <span className='text-sm font-medium'>Notes</span>
        <textarea
          name='notes'
          value={formData.notes || ''}
          onChange={handleChange}
          className='p-2 border rounded w-full'
        />
      </label>

      <button
        type='submit'
        className='px-4 py-2 bg-green-600 text-white rounded'
      >
        Next
      </button>
    </form>
  );
};

export default EventStepGeneral;
